import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, ChevronLeft, ChevronRight } from 'lucide-react';
import { Dialog, DialogContent } from '../components/ui/dialog';

export default function Gallery() {
  const [galleryData, setGalleryData] = useState({ images: [] });
  const [selectedImage, setSelectedImage] = useState(null);
  const [selectedIndex, setSelectedIndex] = useState(0);

  useEffect(() => {
    fetch('/data/gallery.json')
      .then(res => res.json())
      .then(data => setGalleryData(data))
      .catch(err => console.error('Error loading gallery:', err));
  }, []);

  const displayImages = galleryData.images.filter(img => img.category !== 'floorplan').slice(0, 12);

  const openModal = (image, index) => {
    setSelectedImage(image);
    setSelectedIndex(index);
  };

  const closeModal = () => {
    setSelectedImage(null);
  };

  const goToPrev = () => {
    const newIndex = selectedIndex === 0 ? displayImages.length - 1 : selectedIndex - 1;
    setSelectedIndex(newIndex);
    setSelectedImage(displayImages[newIndex]);
  };

  const goToNext = () => {
    const newIndex = selectedIndex === displayImages.length - 1 ? 0 : selectedIndex + 1;
    setSelectedIndex(newIndex);
    setSelectedImage(displayImages[newIndex]);
  };

  return (
    <section
      id="gallery"
      data-testid="gallery-section"
      className="py-10 sm:py-12 md:py-16 bg-[#F8F9FA] lg:mr-72 xl:mr-80"
    >
      <div className="max-w-5xl mx-auto px-4">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-8 sm:mb-10"
        >
          <h2 className="font-serif text-xl sm:text-2xl md:text-3xl text-[#9A7B4F] mb-2">
            Project Gallery
          </h2>
          <div className="w-12 sm:w-16 h-1 bg-[#9A7B4F] mx-auto" />
        </motion.div>

        {/* Image Grid */}
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2 sm:gap-3">
          {displayImages.map((image, index) => (
            <motion.div
              key={image.id}
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: index * 0.03 }}
              className="cursor-pointer group relative aspect-square overflow-hidden"
              onClick={() => openModal(image, index)}
            >
              <img
                src={image.src}
                alt={image.alt}
                className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors" />
            </motion.div>
          ))}
        </div>

        {/* Modal */}
        <Dialog open={!!selectedImage} onOpenChange={closeModal}>
          <DialogContent className="max-w-4xl bg-white border-gray-200 p-0 overflow-hidden mx-4">
            <div className="relative">
              <button onClick={closeModal} className="absolute top-2 sm:top-4 right-2 sm:right-4 z-10 p-1.5 sm:p-2 bg-white shadow-md hover:bg-gray-100">
                <X className="w-4 h-4 sm:w-5 sm:h-5 text-gray-700" />
              </button>
              <button onClick={goToPrev} className="absolute left-2 sm:left-4 top-1/2 -translate-y-1/2 z-10 p-1.5 sm:p-2 bg-white shadow-md hover:bg-[#9A7B4F] hover:text-white">
                <ChevronLeft className="w-5 h-5 sm:w-6 sm:h-6" />
              </button>
              <button onClick={goToNext} className="absolute right-2 sm:right-4 top-1/2 -translate-y-1/2 z-10 p-1.5 sm:p-2 bg-white shadow-md hover:bg-[#9A7B4F] hover:text-white">
                <ChevronRight className="w-5 h-5 sm:w-6 sm:h-6" />
              </button>
              <AnimatePresence mode="wait">
                {selectedImage && (
                  <motion.img
                    key={selectedImage.id}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    src={selectedImage.src}
                    alt={selectedImage.alt}
                    className="w-full h-auto max-h-[70vh] sm:max-h-[80vh] object-contain"
                  />
                )}
              </AnimatePresence>
              <div className="p-3 sm:p-4 bg-white border-t border-gray-100">
                <p className="text-gray-700 text-xs sm:text-sm">{selectedImage?.alt}</p>
                <p className="text-gray-400 text-[10px] sm:text-xs mt-1">{selectedIndex + 1} / {displayImages.length}</p>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </section>
  );
}
